import { tableEditing, columnResizing, CellSelection, } from 'prosemirror-tables';
import { DOMType, TextSelection } from '@tencent/exeditor3-core';
import { createIcon } from '@tencent/exeditor3-ui-kit/dist/icon.js';
import { dependencies } from './dependencies.js';
import { tableOperations } from './operations.js';
import { getTableNodeTypes, createCell } from './helpers.js';
import { getTableView } from './table-view.js';
import '@tencent/exeditor3-ui-kit/dist/toolbar-button.js';
import './table-panel.js';
export class TablesPlugin {
    constructor() {
        this.name = 'table';
        this.spec = {
            type: DOMType.BLOCK,
            childrenType: 'tableRow+',
            matchSelector: 'table',
            tableRole: 'table',
            isolating: true,
            attrs: {
                builtIns: {
                    toDOMAttribute: () => ({ class: 'tableWrapper' }),
                },
            },
            toDOM: {
                tag: 'div',
                children: {
                    tag: 'table',
                    attributes: {},
                    children: {
                        tag: 'tbody',
                        attributes: {},
                        children: true,
                    },
                },
            },
            dependencies,
            renderAttrsToEditView: true,
        };
        this.addColumnBefore = () => this.editor.execute(tableOperations.column.add_before);
        this.addColumnAfter = () => this.editor.execute(tableOperations.column.add_after);
        this.deleteColumn = () => this.editor.execute(tableOperations.column.delete);
        this.addRowBefore = () => this.editor.execute(tableOperations.row.add_before);
        this.addRowAfter = () => this.editor.execute(tableOperations.row.add_after);
        this.deleteRow = () => this.editor.execute(tableOperations.row.delete);
        this.deleteTable = () => this.editor.execute(tableOperations.table.delete);
        this.mergeCells = () => this.editor.execute(tableOperations.cell.merge);
        this.splitCell = () => this.editor.execute(tableOperations.cell.split);
        this.toggleHeaderColumn = () => this.editor.execute(tableOperations.header_column.toggle);
        this.toggleHeaderRow = () => this.editor.execute(tableOperations.header_row.toggle);
        this.toggleHeaderCell = () => this.editor.execute(tableOperations.header_cell.toggle);
        this.goToNextCell = () => this.editor.execute(tableOperations.cell.next);
        this.goToPreviousCell = () => this.editor.execute(tableOperations.cell.prev);
        this.deleteTableWhenAllCellsSelected = () => {
            const selection = this.editor.selection().raw;
            if (!(selection instanceof CellSelection)) {
                return false;
            }
            let cellCount = 0;
            let tableNode = null;
            const { $from } = selection.ranges[0];
            for (let i = $from.depth; i > 0; i -= 1) {
                const node = $from.node(i);
                if (node.type.name === 'table') {
                    tableNode = node;
                    break;
                }
            }
            if (!tableNode) {
                return false;
            }
            tableNode.descendants((node) => {
                if (/cell|header_cell/.test(node.type.spec.tableRole)) {
                    cellCount += 1;
                    return false;
                }
                return true;
            });
            const allCellsSelected = cellCount === selection.ranges.length;
            // 只处理 table cells 全选中的状态
            if (!allCellsSelected) {
                return false;
            }
            // 如果 table 内有文字，则清空 table 并保持其全选状态
            if (tableNode.textContent) {
                this.editor.dispatch((tr) => {
                    const { $anchorCell, $headCell } = tr.selection;
                    tr.deleteSelection();
                    tr.setSelection(new CellSelection(tr.doc.resolve(tr.mapping.map($anchorCell.pos)), tr.doc.resolve(tr.mapping.map($headCell.pos))));
                });
                return true;
            }
            this.deleteTable();
            return true;
        };
    }
    initEditor(editor) {
        this.editor = editor;
    }
    capabilities() {
        return {
            commands: {
                insertTable: this.insertTable,
                addColumnBefore: this.addColumnBefore,
                addColumnAfter: this.addColumnAfter,
                deleteColumn: this.deleteColumn,
                addRowBefore: this.addRowBefore,
                addRowAfter: this.addRowAfter,
                deleteRow: this.deleteRow,
                deleteTable: this.deleteTable,
                mergeCells: this.mergeCells,
                splitCell: this.splitCell,
                toggleHeaderColumn: this.toggleHeaderColumn,
                toggleHeaderRow: this.toggleHeaderRow,
                toggleHeaderCell: this.toggleHeaderCell,
                goToNextCell: this.goToNextCell,
                goToPreviousCell: this.goToPreviousCell,
            },
            shortcuts: {
                Tab: this.goToNextCell,
                'Shift-Tab': this.goToPreviousCell,
                Backspace: this.deleteTableWhenAllCellsSelected,
                'Mod-Backspace': this.deleteTableWhenAllCellsSelected,
                Delete: this.deleteTableWhenAllCellsSelected,
                'Mod-Delete': this.deleteTableWhenAllCellsSelected,
            },
            prosemirrorPlugins: [
                tableEditing(),
                columnResizing({
                    handleWidth: 5,
                    cellMinWidth: 25,
                    View: getTableView(this),
                }),
            ],
        };
    }
    renderToolbarItem() {
        const btn = document.createElement('exeditor-toolbar-button');
        btn.label = this.editor.t('表格');
        const icon = createIcon({
            name: this.name,
            editor: this.editor,
            fallback: () => import('./icon.js').then((m) => m.tableIcon),
        });
        const panel = document.createElement('exeditor-toolbar-table-panel');
        panel.t = this.editor.t.bind(this.editor);
        panel.slot = 'pop-content';
        panel.addEventListener('select', (event) => {
            this.editor.focus();
            this.insertTable(event.detail);
            btn.closeDropdown();
        });
        btn.appendChild(icon);
        btn.appendChild(panel);
        btn.addEventListener('dropdowntoggle', (event) => {
            if (event.detail.value === false) {
                panel.resetPicker();
            }
        });
        return btn;
    }
    insertTable(options = { rows: 3, cols: 3, withHeaderRow: true }) {
        const { rows = 3, cols = 3, withHeaderRow = true } = options;
        return this.createTable(rows, cols, withHeaderRow);
    }
    createTable(rowsCount, colsCount, withHeaderRow, cellContent) {
        return this.editor.execute((state, dispatch) => {
            const types = getTableNodeTypes(this.editor.schemaInstance);
            const headerCells = [];
            const cells = [];
            for (let index = 0; index < colsCount; index += 1) {
                const cell = createCell(types.cell, cellContent);
                if (cell) {
                    cells.push(cell);
                }
                if (withHeaderRow) {
                    const headerCell = createCell(types.header_cell, cellContent);
                    if (headerCell) {
                        headerCells.push(headerCell);
                    }
                }
            }
            const rows = [];
            for (let index = 0; index < rowsCount; index += 1) {
                rows.push(types.row.createChecked(null, withHeaderRow && index === 0 ? headerCells : cells));
            }
            const node = types.table.createChecked(null, rows);
            const { tr } = state;
            const offset = tr.selection.anchor + 1;
            tr.replaceSelectionWith(node)
                .scrollIntoView()
                .setSelection(TextSelection.near(tr.doc.resolve(offset)));
            dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr);
            return true;
        });
    }
}
//# sourceMappingURL=index.js.map